
import React, { useState } from 'react';
import { Editor } from '@tiptap/react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Bold, 
  Italic, 
  Underline as UnderlineIcon,
  Strikethrough,
  Heading, 
  List, 
  ListOrdered, 
  Link as LinkIcon, 
  Code, 
  Quote, 
  Info, 
  AlertTriangle,
  Minus,
  Table as TableIcon,
  CheckSquare,
  Palette,
  Terminal
} from 'lucide-react';
import { HIGHLIGHT_COLORS } from './EditorConstants';

interface ToolbarActionsProps {
  editor: Editor;
}

const ToolbarActions: React.FC<ToolbarActionsProps> = ({ editor }) => {
  const [linkUrl, setLinkUrl] = useState('');
  const [isLinkOpen, setIsLinkOpen] = useState(false);

  const handleLinkSubmit = () => {
    if (linkUrl) {
      editor.chain().focus().setLink({ href: linkUrl }).run();
      setLinkUrl('');
      setIsLinkOpen(false);
    }
  };

  const insertNoteBlock = (type: 'info' | 'warning') => {
    const icon = type === 'info' ? '💡' : '⚠️';
    const className = type === 'info' 
      ? 'bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-950 dark:border-blue-800 dark:text-blue-200'
      : 'bg-yellow-50 border-yellow-200 text-yellow-800 dark:bg-yellow-950 dark:border-yellow-800 dark:text-yellow-200';
    
    const noteHTML = `<div class="note-block ${className} border-l-4 p-4 my-4 rounded-r-lg"><div class="flex items-start gap-2"><span class="text-lg">${icon}</span><div><strong>${type.charAt(0).toUpperCase() + type.slice(1)}:</strong> Add your note here...</div></div></div>`;
    
    editor.chain().focus().insertContent(noteHTML).run();
  };

  const insertTable = () => {
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  };

  const handleToolbarAction = (action: () => void) => {
    action();
    setTimeout(() => {
      editor.commands.focus();
    }, 0);
  };

  const ToolbarButton: React.FC<{
    onClick: () => void;
    isActive?: boolean;
    disabled?: boolean;
    children: React.ReactNode;
    title: string;
  }> = ({ onClick, isActive = false, disabled = false, children, title }) => (
    <Button
      variant={isActive ? "default" : "ghost"}
      size="sm"
      onMouseDown={(e) => {
        e.preventDefault();
      }}
      onClick={() => handleToolbarAction(onClick)}
      disabled={disabled}
      title={title}
      className="h-8 w-8 p-0"
    >
      {children}
    </Button>
  );

  return (
    <div className="flex flex-wrap gap-1 items-center">
      {/* Text Formatting */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        isActive={editor.isActive('bold')}
        title="Bold (Ctrl+B)"
      >
        <Bold size={14} />
      </ToolbarButton>
      
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        isActive={editor.isActive('italic')}
        title="Italic (Ctrl+I)"
      >
        <Italic size={14} />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        isActive={editor.isActive('underline')}
        title="Underline (Ctrl+U)"
      >
        <UnderlineIcon size={14} />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleStrike().run()}
        isActive={editor.isActive('strike')}
        title="Strikethrough"
      >
        <Strikethrough size={14} />
      </ToolbarButton>

      {/* Highlight with Color Picker */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant={editor.isActive('highlight') ? "default" : "ghost"}
            size="sm"
            className="h-8 w-8 p-0"
            title="Highlight Text"
          >
            <Palette size={14} />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-3">
          <div className="space-y-2">
            <h4 className="font-medium text-sm">Highlight Colors</h4>
            <div className="grid grid-cols-3 gap-2">
              {HIGHLIGHT_COLORS.map((color) => (
                <button
                  key={color.value}
                  onClick={() => {
                    editor.chain().focus().toggleHighlight({ color: color.value }).run();
                  }}
                  className="flex flex-col items-center p-2 rounded border hover:bg-gray-50 dark:hover:bg-gray-800"
                  title={`${color.name} - ${color.label}`}
                >
                  <div
                    className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600"
                    style={{ backgroundColor: color.value }}
                  />
                  <span className="text-xs mt-1">{color.name}</span>
                </button>
              ))}
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => editor.chain().focus().unsetHighlight().run()}
              className="w-full"
            >
              Remove Highlight
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      <Separator orientation="vertical" className="h-6 mx-1" />

      {/* Headings */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        isActive={editor.isActive('heading', { level: 2 })}
        title="Heading 2"
      >
        <Heading size={14} />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        isActive={editor.isActive('heading', { level: 3 })}
        title="Heading 3"
      >
        <span className="text-xs font-bold">H3</span>
      </ToolbarButton>

      <Separator orientation="vertical" className="h-6 mx-1" />

      {/* Lists */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        isActive={editor.isActive('bulletList')}
        title="Bullet List"
      >
        <List size={14} />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        isActive={editor.isActive('orderedList')}
        title="Numbered List"
      >
        <ListOrdered size={14} />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleTaskList().run()}
        isActive={editor.isActive('taskList')}
        title="Task List"
      >
        <CheckSquare size={14} />
      </ToolbarButton>

      <Separator orientation="vertical" className="h-6 mx-1" />

      {/* Code */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleCode().run()}
        isActive={editor.isActive('code')}
        title="Inline Code"
      >
        <Code size={14} />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        isActive={editor.isActive('codeBlock')}
        title="Code Block"
      >
        <Terminal size={14} />
      </ToolbarButton>

      <Separator orientation="vertical" className="h-6 mx-1" />

      {/* Quote & Elements */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        isActive={editor.isActive('blockquote')}
        title="Blockquote"
      >
        <Quote size={14} />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
        title="Horizontal Rule"
      >
        <Minus size={14} />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => insertTable()}
        isActive={editor.isActive('table')}
        title="Insert Table"
      >
        <TableIcon size={14} />
      </ToolbarButton>

      {/* Link */}
      <Popover open={isLinkOpen} onOpenChange={setIsLinkOpen}>
        <PopoverTrigger asChild>
          <Button
            variant={editor.isActive('link') ? "default" : "ghost"}
            size="sm"
            className="h-8 w-8 p-0"
            title="Add Link"
          >
            <LinkIcon size={14} />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-4">
          <div className="space-y-2">
            <Label htmlFor="link-url">URL</Label>
            <Input
              id="link-url"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://example.com"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleLinkSubmit();
                }
              }}
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={handleLinkSubmit}>
                Add Link
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => editor.chain().focus().unsetLink().run()}
              >
                Remove Link
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      <Separator orientation="vertical" className="h-6 mx-1" />

      {/* Note Blocks */}
      <ToolbarButton
        onClick={() => insertNoteBlock('info')}
        title="Insert Info Note"
      >
        <Info size={14} />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => insertNoteBlock('warning')}
        title="Insert Warning Note"
      >
        <AlertTriangle size={14} />
      </ToolbarButton>
    </div>
  );
};

export default ToolbarActions;
